import { useState, useEffect } from 'react'
import { PlusIcon, XMarkIcon, BuildingOfficeIcon, TruckIcon, ExclamationTriangleIcon, MagnifyingGlassIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline'
import { norm } from '../../utils/normalize'
import SortableHeader, { useSort } from '../../components/SortableHeader'

interface Supplier {
  id: number
  trade_name: string
  nif: string
  address: string
  phone: string
  sanitary_register: string
  email?: string
  webpage?: string
  contact_person?: string
  is_self: boolean
  logo?: string | null
  ingredients_count?: number
  created_at: string
  updated_at: string
}

const API_BASE_URL = '/api/v1/dev'

export default function Suppliers() {
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [formData, setFormData] = useState({
    trade_name: '',
    nif: '',
    address: '',
    phone: '',
    sanitary_register: '',
    email: '',
    webpage: '',
    contact_person: '',
    is_self: false,
    logo: '' as string | null
  })

  // Debug: Verify this is the correct component
  console.log('🔥 SUPPLIERS COMPONENT LOADED - CONNECTED TO API', new Date().toISOString())

  // Fetch suppliers from API
  const fetchSuppliers = async () => {
    try {
      setLoading(true)
      setError(null)
      console.log('🔄 FETCHING SUPPLIERS FROM API:', `${API_BASE_URL}/suppliers`)
      const response = await fetch(`${API_BASE_URL}/suppliers`)
      const data = await response.json()
      console.log('📥 API RESPONSE:', data)

      if (response.ok) {
        setSuppliers(data.data)
        console.log('✅ SUPPLIERS LOADED:', data.data.length, 'items')
      } else {
        setError('Error al cargar proveedores')
        console.error('❌ API ERROR:', data)
      }
    } catch (err) {
      setError('Error de conexión con el servidor')
      console.error('❌ FETCH ERROR:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSuppliers()
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)

    try {
      let response
      const body = {
        trade_name: formData.trade_name,
        nif: formData.nif || null,
        address: formData.address || null,
        phone: formData.phone || null,
        sanitary_register: formData.sanitary_register || null,
        email: formData.email || null,
        webpage: formData.webpage || null,
        contact_person: formData.contact_person || null,
        is_self: formData.is_self,
        logo: formData.logo || null
      }

      if (editingSupplier) {
        // Update supplier
        response = await fetch(`${API_BASE_URL}/suppliers/${editingSupplier.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        })
      } else {
        // Create new supplier
        response = await fetch(`${API_BASE_URL}/suppliers`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        })
      }

      const data = await response.json()

      if (response.ok) {
        // Refresh the suppliers list
        await fetchSuppliers()
        closeModal()
      } else {
        if (data.errors) {
          // Display validation errors
          const errorMessages = Object.values(data.errors).flat().join(', ')
          setError(errorMessages)
        } else {
          setError(data.message || 'Error al guardar proveedor')
        }
      }
    } catch (err) {
      setError('Error de conexión con el servidor')
      console.error('Error saving supplier:', err)
    } finally {
      setSaving(false)
    }
  }

  const openModal = (supplier?: Supplier) => {
    if (supplier) {
      setEditingSupplier(supplier)
      setFormData({
        trade_name: supplier.trade_name,
        nif: supplier.nif || '',
        address: supplier.address || '',
        phone: supplier.phone || '',
        sanitary_register: supplier.sanitary_register || '',
        email: supplier.email || '',
        webpage: supplier.webpage || '',
        contact_person: supplier.contact_person || '',
        is_self: supplier.is_self,
        logo: supplier.logo || ''
      })
    } else {
      setEditingSupplier(null)
      setFormData({
        trade_name: '',
        nif: '',
        address: '',
        phone: '',
        sanitary_register: '',
        email: '',
        webpage: '',
        contact_person: '',
        is_self: false,
        logo: ''
      })
    }
    setShowModal(true)
  }

  const closeModal = () => {
    setShowModal(false)
    setEditingSupplier(null)
    setFormData({
      trade_name: '',
      nif: '',
      address: '',
      phone: '',
      sanitary_register: '',
      email: '',
      webpage: '',
      contact_person: '',
      is_self: false,
      logo: ''
    })
    setError(null)
  }

  const deleteSupplier = async (id: number) => {
    if (!confirm('¿Está seguro de eliminar este proveedor?')) {
      return
    }

    try {
      const response = await fetch(`${API_BASE_URL}/suppliers/${id}`, {
        method: 'DELETE',
      })

      const data = await response.json()

      if (response.ok) {
        // Refresh the suppliers list
        await fetchSuppliers()
      } else {
        setError(data.message || 'Error al eliminar proveedor')
      }
    } catch (err) {
      setError('Error de conexión con el servidor')
      console.error('Error deleting supplier:', err)
    }
  }

  const [currentPage, setCurrentPage] = useState(1)
  const perPage = 15
  const { sortField, sortDirection, handleSort, sortData } = useSort<Supplier>('trade_name')

  const filteredSuppliers = suppliers.filter((s) => {
    const q = norm(search)
    return norm(s.trade_name).includes(q) || norm(s.nif).includes(q) || (s.phone && norm(s.phone).includes(q))
  })

  const sortedSuppliers = sortData(filteredSuppliers)
  const totalPages = Math.ceil(sortedSuppliers.length / perPage)
  const paginatedSuppliers = sortedSuppliers.slice((currentPage - 1) * perPage, currentPage * perPage)

  useEffect(() => { setCurrentPage(1) }, [search])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  const selfSupplier = suppliers.find(s => s.is_self)
  const externalSuppliers = suppliers.filter(s => !s.is_self)

  return (
    <div>
      <div className="mb-8">
        <div className="md:flex md:items-center md:justify-between">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold text-gray-900">Gestión de Proveedores</h1>
            <p className="mt-1 text-sm text-gray-600">
              Registro y gestión de proveedores con registros sanitarios
            </p>
          </div>
          <div className="mt-4 flex md:mt-0 md:ml-4">
            <button
              type="button"
              onClick={() => openModal()}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
              Nuevo Proveedor
            </button>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
            <div className="ml-auto pl-3">
              <button
                onClick={() => setError(null)}
                className="text-red-400 hover:text-red-600"
              >
                <span className="sr-only">Cerrar</span>
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Info Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 mb-8">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <BuildingOfficeIcon className="h-8 w-8 text-blue-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Negocio Propio</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {selfSupplier ? selfSupplier.trade_name : 'No configurado'}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TruckIcon className="h-8 w-8 text-green-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Proveedores Externos</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {externalSuppliers.length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
                  <span className="text-white font-bold text-sm">T</span>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Proveedores</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {suppliers.length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Alert about supplier management */}
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <BuildingOfficeIcon className="h-5 w-5 text-blue-400" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              <strong>Gestión de proveedores:</strong> Mantén actualizados los registros sanitarios
              y datos de contacto. El proveedor marcado como "Negocio Propio" representa tu empresa.
            </p>
          </div>
        </div>
      </div>

      {/* Suppliers Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">
            Proveedores Registrados ({filteredSuppliers.length}{search ? ` de ${suppliers.length}` : ''})
          </h3>
          <div className="relative">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre, NIF o teléfono..."
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500 w-72"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortableHeader label="Nombre Comercial" field="trade_name" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <SortableHeader label="NIF/CIF" field="nif" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Registro Sanitario
                </th>
                <SortableHeader label="Contacto" field="phone" currentSort={sortField} currentDirection={sortDirection} onSort={handleSort} />
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="relative px-6 py-3">
                  <span className="sr-only">Acciones</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200 table-hover">
              {paginatedSuppliers.map((supplier) => (
                <tr key={supplier.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{supplier.trade_name}</div>
                    <div className="text-sm text-gray-500">{supplier.address}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-mono text-gray-900">{supplier.nif}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-mono text-gray-900">{supplier.sanitary_register}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{supplier.phone}</div>
                    {supplier.contact_person && (
                      <div className="text-sm text-gray-700">{supplier.contact_person}</div>
                    )}
                    <div className="text-sm text-gray-500">{supplier.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {supplier.is_self ? (
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        PROPIO
                      </span>
                    ) : (
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                        EXTERNO
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => openModal(supplier)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => deleteSupplier(supplier.id)}
                        className="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={supplier.is_self}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Página {currentPage} de {totalPages} ({filteredSuppliers.length} proveedores)
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border rounded-md text-sm disabled:opacity-50 hover:bg-gray-50"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border rounded-md text-sm disabled:opacity-50 hover:bg-gray-50"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:w-11/12 max-w-4xl">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {editingSupplier ? 'Editar Proveedor' : 'Nuevo Proveedor'}
                    </h3>
                    <button
                      type="button"
                      onClick={closeModal}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Nombre Comercial *
                      </label>
                      <input
                        type="text"
                        value={formData.trade_name}
                        onChange={(e) => setFormData({ ...formData, trade_name: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder="Nombre de la empresa o negocio"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        NIF/CIF *
                      </label>
                      <input
                        type="text"
                        value={formData.nif}
                        onChange={(e) => setFormData({ ...formData, nif: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder="ej: B12345678"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Dirección *
                      </label>
                      <textarea
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        rows={2}
                        placeholder="Dirección completa"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Teléfono *
                        </label>
                        <input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          placeholder="+34 91 123 4567"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Email
                        </label>
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                          placeholder="contacto@empresa.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Página web
                        </label>
                        <input
                          type="text"
                          value={formData.webpage}
                          onChange={(e) => setFormData({ ...formData, webpage: e.target.value })}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm placeholder:italic placeholder:text-gray-300"
                          placeholder="www.ejemplo.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Persona de contacto
                        </label>
                        <input
                          type="text"
                          value={formData.contact_person}
                          onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                          className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm placeholder:italic placeholder:text-gray-300"
                          placeholder="Nombre y apellidos"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Registro Sanitario *
                      </label>
                      <input
                        type="text"
                        value={formData.sanitary_register}
                        onChange={(e) => setFormData({ ...formData, sanitary_register: e.target.value })}
                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                        placeholder="ej: RS-28.12345/M"
                        required
                      />
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="is_self"
                        checked={formData.is_self}
                        onChange={(e) => setFormData({ ...formData, is_self: e.target.checked })}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <label htmlFor="is_self" className="ml-2 block text-sm text-gray-900">
                        Es mi negocio propio
                      </label>
                    </div>

                    {formData.is_self && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Logo (para fichas técnicas y etiquetas)
                        </label>
                        <div className="flex items-center gap-3">
                          {formData.logo
                            ? <img src={formData.logo} alt="logo" className="h-16 w-16 object-contain border border-gray-200 rounded" />
                            : <div className="h-16 w-16 flex items-center justify-center border border-dashed border-gray-300 rounded text-xs text-gray-400">Sin logo</div>}
                          <div className="flex flex-col gap-1">
                            <input
                              type="file"
                              accept="image/png,image/jpeg,image/webp,image/svg+xml"
                              onChange={(e) => {
                                const file = e.target.files?.[0]
                                if (!file) return
                                if (file.size > 1024 * 1024) { setError('El logo no debe superar 1 MB'); return }
                                const reader = new FileReader()
                                reader.onload = () => setFormData(fd => ({ ...fd, logo: String(reader.result) }))
                                reader.readAsDataURL(file)
                              }}
                              className="text-sm"
                            />
                            {formData.logo && (
                              <button type="button" onClick={() => setFormData(fd => ({ ...fd, logo: '' }))} className="text-xs text-red-600 hover:text-red-800 text-left">
                                Quitar logo
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Error display in modal */}
                    {error && (
                      <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-3">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                          </div>
                          <div className="ml-3">
                            <p className="text-sm text-red-700">{error}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    disabled={saving}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Guardando...
                      </div>
                    ) : (
                      editingSupplier ? 'Actualizar' : 'Crear'
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}